import { PALETTE } from '@constants/colors';

export function getColor(
  colorName: (typeof PALETTE)[keyof typeof PALETTE],
  opacity = 1
) {
  try {
    const colorValue = getComputedStyle(document.documentElement)
      .getPropertyValue(`--${colorName}`)
      .trim();
    
    if (!colorValue) {
      return `rgba(255, 0, 15 / ${opacity})`;
    }
    
    return `rgba(${colorValue} / ${opacity})`;
  } catch (error) {
    return `rgba(255, 0, 15 / ${opacity})`;
  }
}

export function getColorHex(colorName: (typeof PALETTE)[keyof typeof PALETTE]) {
  const computedStyle = getComputedStyle(document.documentElement);
  const rawValue = computedStyle.getPropertyValue(`--${colorName}`);
  return `#${rawValue
    .split(' ')
    .map((part) => Number(part).toString(16).padStart(2, '0'))
    .join('')}`;
}

export function getColorHexWithOpacity(
  colorName: (typeof PALETTE)[keyof typeof PALETTE],
  opacity: number
) {
  if (opacity >= 0 && opacity <= 1) {
    const hexValue = Math.round(opacity * 255)
      .toString(16)
      .toUpperCase();
    return getColorHex(colorName).concat(hexValue.padStart(2, '0'));
  } else {
    throw new Error('Invalid opacity value (it should be between 0 and 1)');
  }
}

// Function to generate tones and shades of a given RGB color
export function generateTonesAndShades(
  color: (typeof PALETTE)[keyof typeof PALETTE],
  numColors: number
): string[] {
  var baseColor = getColor(color);
  const baseRgb = parseRgb(baseColor);
  const { r, g, b } = baseRgb;
  const [h, s, l] = rgbToHsl(r, g, b);
  const colors = [];

  for (let i = 0; i < numColors; i++) {
    const newS = Math.random() * 50 + 50; // Saturation between 50% and 100%
    const newL = Math.random() * 40 + 30; // Lightness between 30% and 70%
    const [newR, newG, newB] = hslToRgb(h, newS, newL);
    colors.push(`rgb(${newR}, ${newG}, ${newB})`);
  }

  return colors;
}

export function parseRgb(rgb: string): { r: number; g: number; b: number } {
  const result = rgb.match(/\d+/g);
  if (result) {
    return {
      r: parseInt(result[0], 10),
      g: parseInt(result[1], 10),
      b: parseInt(result[2], 10),
    };
  }
  throw new Error('Invalid RGB color format');
}

// Function to convert RGB to HSL
function rgbToHsl(r: number, g: number, b: number): number[] {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return [h * 360, s * 100, l * 100];
}

// Function to convert HSL to RGB
function hslToRgb(h: number, s: number, l: number): number[] {
  h /= 360;
  s /= 100;
  l /= 100;
  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

export function convertToRGBNumbersFormat(color: string) {
  // Convert RGBA to RGB format
  const rgba = color.match(/\d+/g); // Extract numbers from the rgba string
  if (rgba) {
    const rgb = `${rgba[0]} ${rgba[1]} ${rgba[2]}`; // Format as "R G B"
    return rgb; // Output: "155 30 0"
  }
  return null;
}

export function convertToRGBNumbersAlphaFormat(color: string) {
  // Convert RGBA to RGB format
  const rgba = color.match(/[\d.]+/g); // Extract numbers from the rgba string
  if (rgba) {
    const rgb = `${rgba[0]} ${rgba[1]} ${rgba[2]}`;
    return { rgb, alpha: rgba[3] ?? 1 };
  }
  return null;
}

export function convertToHEXFormat(color: string) {
  const rgba = color.match(/[\d.]+/g);
  if (rgba) {
    const [r, g, b] = rgba.map(Number);
    const red = Math.round(r).toString(16).padStart(2, '0');
    const green = Math.round(g).toString(16).padStart(2, '0');
    const blue = Math.round(b).toString(16).padStart(2, '0');
    return `#${red}${green}${blue}`;
  }
  return null;
}

export function lightenColor(rgb: Array<number>, factor: number) {
  const [r, g, b] = rgb;
  const newR = Math.min(255, Math.round(r + (255 - r) * factor));
  const newG = Math.min(255, Math.round(g + (255 - g) * factor));
  const newB = Math.min(255, Math.round(b + (255 - b) * factor));
  return [newR, newG, newB];
}

export function darkenColor(rgb: Array<number>, factor: number) {
  const [r, g, b] = rgb;
  const newR = Math.round(r * (1 - factor));
  const newG = Math.round(g * (1 - factor));
  const newB = Math.round(b * (1 - factor));
  return [newR, newG, newB];
}
